/**
 * UnidadRepository - Repositorio unificado para Unidades
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { IUnidadRepository } from '../interfaces/IUnidadRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { Unidad } from '../interfaces/entities';

/**
 * Repositorio unificado de Unidades
 *
 * Soft-delete: las unidades NO se borran físicamente; se marca `is_deleted=true`
 * (UPDATE) para que el catch-up incremental las propague a todos los clientes
 * (online u offline). Las lecturas filtran `is_deleted=false`.
 */
export class UnidadRepository extends BaseRepository<Unidad, string> implements IUnidadRepository {

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'unidades', 'unidades');

    // Habilitar soft-delete: getAll() filtra is_deleted=false + métodos heredados.
    this.supportsSoftDelete = true;

    // Sin profileRepo, getAllForActiveEmpresa() no podría resolver la empresa
    // activa y devolvería unidades de TODAS las empresas (ver BaseRepository).
    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<Unidad, 'id'>): Promise<string> {
    const id = uuidv4();
    const record: Unidad = { ...data, id, is_deleted: false } as Unidad;
    await this.db.insert('unidades', record);
    this.emitSync('INSERT', record);
    return id;
  }

  /**
   * Override de getAll(): excluye is_deleted=true con filtro falsy
   * (`is_deleted !== true`). Igual que AnimalRepository: filas creadas
   * localmente pueden tener `is_deleted=undefined` (legacy) y deben seguir
   * visibles; sólo se ocultan las marcadas true.
   */
  async getAll(): Promise<Unidad[]> {
    const all = await this.db.getAll<Unidad>('unidades');
    return all.filter(u => !(u as any)?.is_deleted);
  }

  async update(data: Partial<Unidad> & { id: string }): Promise<void> {
    await this.db.update('unidades', data.id, data);
    this.emitSync('UPDATE', data as any);
  }

  async delete(id: string): Promise<void> {
    // Soft-delete: marca is_deleted=true (UPDATE) en vez de borrar físicamente.
    await this.softDelete(id);
  }

  async getByNombre(nombre: string): Promise<Unidad[]> {
    return this.db.queryMultiple<Unidad>('unidades', [
      this.NOT_DELETED,
      { field: 'nombre', operator: 'includes', value: nombre.toLowerCase() }
    ]);
  }

  async getByEmpresaId(empresaId: number): Promise<Unidad[]> {
    return this.db.queryMultiple<Unidad>('unidades', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async getNextId(): Promise<string> {
    const all = await this.getAll();
    if (all.length === 0) return '1';

    const maxId = Math.max(...all.map(u => parseInt(u.id) || 0));
    return String(maxId + 1);
  }

  // ==================== Bulk Operations ====================

  async bulkAdd(items: Unidad[]): Promise<void> {
    await this.db.bulkInsert('unidades', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkAddSilent(items: Unidad[]): Promise<void> {
    await this.db.bulkInsert('unidades', items);
    items.forEach(item => this.emitLocalSync('INSERT', item));
  }

  async bulkPut(items: Unidad[]): Promise<void> {
    await this.db.bulkUpsert('unidades', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkPutSilent(items: Unidad[]): Promise<void> {
    await this.db.bulkUpsert('unidades', items);
    items.forEach(item => this.emitLocalSync('UPDATE', item));
  }

  /**
   * Bulk SOFT-delete: marca is_deleted=true (UPDATE) para cada id, en lugar de
   * borrar físicamente. Delega en `bulkUpdate` (bulkUpdate local + un único
   * evento BULK_UPDATE → upsert en Supabase llevando is_deleted=true).
   * Emite además eventos locales UPDATE por item para refrescar la UI.
   */
  async bulkDelete(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const now = this.getCurrentTimestamp();
    const updates = ids.map(id => ({ id, is_deleted: true, updated_at: now }));
    await this.bulkUpdate(updates as any);
    updates.forEach(u => this.emitLocalSync('UPDATE', u as any));
  }

  async bulkDeleteSilent(ids: string[]): Promise<void> {
    await this.db.bulkDelete('unidades', ids);
    ids.forEach(id => this.emitLocalSync('DELETE', id));
  }

  async getLastByDate(dateColumn: string = 'updated_at'): Promise<Unidad | undefined> {
    const results = await this.db.query<Unidad>('unidades', undefined, {
      column: dateColumn,
      direction: 'desc'
    });
    return results[0] || undefined;
  }
}
